import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { AuthService } from '@services/auth/auth.service';
import { CurrentUserService } from '@services/auth/current-user.service';
import { ViewportService } from '@services/geo/viewport.service';
import { MenuService } from '@services/shared/menu.service';
import { InstallBannerComponent } from '@shared/components/install-banner/install-banner.component';
import { SharedModule } from '@shared/modules/shared.module';
import { ProfileComponent } from 'app/components/backoffice/account/profile/profile.component';
import { BottomNavComponent } from 'app/layout/menu/bottom-nav/bottom-nav.component';
import { ClientNavbarComponent } from 'app/layout/menu/client-navbar/client-navbar.component';
import { ProfileIconBarComponent } from 'app/layout/menu/profile-icon-bar/profile-icon-bar.component';
import { SidebarComponent } from 'app/layout/menu/sidebar/sidebar.component';
import { filter, Subscription } from 'rxjs';

type LayoutMode = 'public' | 'client' | 'client-mobile' | 'backoffice' | 'backoffice-mobile';

// Routes that should have noindex meta tags
const BACKOFFICE_ROUTES = [
	'/orders',
	'/dashboard',
	'/platforms',
	'/products',
	'/requests',
	'/users',
	'/configuration',
	'/account',
];

@Component({
	selector: 'app-main-layout',
	imports: [
		SharedModule,
		RouterOutlet,
		SidebarComponent,
		InstallBannerComponent,
		ProfileIconBarComponent,
		ProfileComponent,
		BottomNavComponent,
		ClientNavbarComponent,
	],
	templateUrl: './main-layout.component.html',
	styleUrl: './main-layout.component.scss',
})
export class MainLayoutComponent implements OnInit, OnDestroy {
	@Output() sidebarToggle = new EventEmitter<boolean>();
	isSidebarOpen = false;
	profileVisible = false;
	pageTitle = '';

	private profileSubscription?: Subscription;
	private routerSubscription?: Subscription;
	private noindexApplied = false;

	private readonly pageTitles: { [key: string]: string } = {
		dashboard: 'pages.dashboard',
		orders: 'pages.orders',
		requests: 'pages.requests',
		users: 'pages.users',
		account: 'pages.account',
		profile: 'pages.profile',
		notifications: 'pages.notifications',
		boost: 'pages.boost',
		configuration: 'pages.configuration',
		roles: 'pages.roles',
		permissions: 'pages.permissions',
	};

	constructor(
		private meta: Meta,
		private router: Router,
		private authService: AuthService,
		private menuService: MenuService,
		private viewportService: ViewportService,
		private currentUserService: CurrentUserService,
		private cdr: ChangeDetectorRef,
	) {}

	ngOnInit(): void {
		// Subscribe to profile open events from sidebar
		this.profileSubscription = this.menuService.profileModalState$.subscribe((visible) => {
			this.profileVisible = visible;
			this.cdr.detectChanges();
		});

		// Apply noindex meta tags conditionally based on route
		this.routerSubscription = this.router.events
			.pipe(filter((event) => event instanceof NavigationEnd))
			.subscribe((event) => {
				const url = (event as NavigationEnd).urlAfterRedirects;
				this.updateNoindexMeta(url);
				this.updatePageTitle(url);
			});

		// Initial meta check and page title
		this.updateNoindexMeta(this.router.url);
		this.updatePageTitle(this.router.url);
	}

	/** Mobile layout (<= 844px), from the shared viewport signal: no resize listener per layout */
	get isMobile(): boolean {
		return this.viewportService.isMobileView();
	}

	get isAuthenticated(): boolean {
		return this.authService.isAuthenticated();
	}

	get isClient(): boolean {
		return this.currentUserService.hasRole('client');
	}

	get layoutMode(): LayoutMode {
		if (!this.isAuthenticated) return 'public';
		if (this.isClient) return this.isMobile ? 'client-mobile' : 'client';
		if (this.isMobile) return 'backoffice-mobile';
		return 'backoffice';
	}

	/** Wrapper classes: the mode's layout plus, on desktop backoffice, the sidebar/boost state */
	layoutClasses(mode: LayoutMode): Record<string, boolean> {
		const backoffice = mode === 'backoffice';
		return {
			layout: true,
			[`layout--${mode}`]: true,
			collapsed: backoffice && !this.isSidebarOpen,
			expanded: backoffice && this.isSidebarOpen,
			'boost-page': backoffice && this.isBoostPage,
		};
	}

	/** Main area classes (only the desktop backoffice reacts to the sidebar state) */
	mainClasses(mode: LayoutMode): Record<string, boolean> {
		const backoffice = mode === 'backoffice';
		return {
			expanded: backoffice && !this.isSidebarOpen,
			collapsed: backoffice && this.isSidebarOpen,
			'boost-page': backoffice && this.isBoostPage,
		};
	}

	get isBoostPage(): boolean {
		return this.router.url.includes('/boost');
	}

	get isDetailPage(): boolean {
		// Detect if current route is a detail page (has :id pattern)
		const url = this.router.url.split('?')[0];
		const segments = url.split('/').filter((s) => s);
		// Check if last segment is a number (ID)
		const lastSegment = segments[segments.length - 1];
		return !Number.isNaN(Number(lastSegment));
	}

	get baseRoute(): string {
		// Get the base route without the ID
		const url = this.router.url.split('?')[0];
		const segments = url.split('/').filter((s) => s);
		segments.pop(); // Remove last segment (the ID)
		return '/' + segments.join('/');
	}

	private readonly backRouteOverrides: { [key: string]: string } = {
		'/products': '/configuration',
	};

	goBack(): void {
		const back = this.backRouteOverrides[this.baseRoute] ?? this.baseRoute;
		this.router.navigate([back]);
	}

	onProfileVisibleChange(visible: boolean): void {
		this.profileVisible = visible;
		this.menuService.setProfileModalState(visible);
	}

	onSidebarToggle(isSidebarOpen: boolean): void {
		this.isSidebarOpen = isSidebarOpen;
	}

	toggleSidebar(): void {
		this.isSidebarOpen = !this.isSidebarOpen;
		this.sidebarToggle.emit(this.isSidebarOpen);
		if (typeof window !== 'undefined' && window.innerWidth <= 844) {
			if (this.isSidebarOpen) {
				this.menuService.openMenu('sidebar');
			} else {
				this.menuService.closeMenu('sidebar');
			}
		}
	}

	isMobileView(): boolean {
		return this.viewportService.isMobileView();
	}

	ngOnDestroy(): void {
		this.profileSubscription?.unsubscribe();
		this.routerSubscription?.unsubscribe();
	}

	private updateNoindexMeta(url: string): void {
		const isBackofficeRoute = BACKOFFICE_ROUTES.some((route) => url.startsWith(route));
		if (isBackofficeRoute && this.isAuthenticated && !this.noindexApplied) {
			this.meta.addTags([
				{ name: 'robots', content: 'noindex, nofollow' },
				{ name: 'googlebot', content: 'noindex, nofollow' },
			]);
			this.noindexApplied = true;
		} else if (!isBackofficeRoute && this.noindexApplied) {
			this.meta.removeTag('name="robots"');
			this.meta.removeTag('name="googlebot"');
			this.noindexApplied = false;
		}
	}

	private updatePageTitle(url: string): void {
		const cleanUrl = url.split('?')[0].split('#')[0];
		const segments = cleanUrl.split('/').filter((s) => s);
		const lastSegment = segments[segments.length - 1] || '';

		// Check if last segment is a number (detail page)
		if (!Number.isNaN(Number(lastSegment))) {
			// It's a detail page
			this.pageTitle = 'Detalle';
		} else if (this.pageTitles[lastSegment]) {
			// Check if we have a predefined title for this route
			this.pageTitle = this.pageTitles[lastSegment];
		} else if (lastSegment) {
			// Generate title from URL segment
			this.pageTitle = lastSegment
				.split('-')
				.map((word) => word.charAt(0).toUpperCase() + word.slice(1))
				.join(' ');
		} else {
			this.pageTitle = 'Dashboard';
		}
	}
}
